import React from 'react';
import { Button, ButtonProps } from 'reakit/Button';
import classNames from 'classnames';

import { Enum, forwardRefWithAs } from '../../common/types';

export const CKButtonVariantColor = Enum(
  'default',
  'green',
  'red',
  'blue',
  'yellow'
);
export type CKButtonVariantColor = Enum<typeof CKButtonVariantColor>;

export const CKButtonVariant = Enum('bold', 'simple', 'tertiary');
export type CKButtonVariant = Enum<typeof CKButtonVariant>;

export interface CKButtonProps
  extends Pick<ButtonProps, 'onClick' | 'disabled' | 'type'> {
  autoFocus?: boolean;
  className?: string;
  isSmall?: boolean;
  isDisabled?: React.InputHTMLAttributes<HTMLInputElement>['disabled'];
  isLoading?: boolean;
  children: React.ReactNode;
  variant?: CKButtonVariant;
  variantColor?: CKButtonVariantColor;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const defaultProps = {
  variant: CKButtonVariant.simple,
  variantColor: CKButtonVariantColor.default,
};

const Spinner = () => {
  return (
    <svg
      version="1.1"
      id="ck-button-spinner"
      width="1.5em"
      height="1.5em"
      viewBox="0 0 50 50"
    >
      <path
        fill="currentColor"
        d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z"
      >
        <animateTransform
          attributeType="xml"
          attributeName="transform"
          type="rotate"
          from="0 25 25"
          to="360 25 25"
          dur="0.5s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  );
};

export const CKButton = forwardRefWithAs<CKButtonProps, 'button'>(
  (
    {
      as: Comp = Button,
      className,
      children,
      leftIcon,
      rightIcon,
      isLoading,
      variant = defaultProps.variant,
      variantColor = defaultProps.variantColor,
      isSmall,
      isDisabled,
      ...props
    },
    ref
  ) => {
    const buttonClass = classNames(
      `ck-button`,
      `ck-button--${variant}--${variantColor.toLowerCase()}`,
      {
        'ck-button--small': isSmall,
      },
      className
    );

    const maybeLeftIcon = leftIcon ? (
      <span className="ck-button--left-icon">{leftIcon}</span>
    ) : null;
    const maybeRightIcon = rightIcon ? (
      <span className="ck-button--right-icon">{rightIcon}</span>
    ) : null;

    const buttonChildrenContent = isLoading ? (
      <Spinner />
    ) : (
      <React.Fragment>
        {maybeLeftIcon}
        <span>{children}</span>
        {maybeRightIcon}
      </React.Fragment>
    );

    return (
      <Comp
        ref={ref}
        disabled={isDisabled || isLoading}
        {...props}
        className={buttonClass}
      >
        {buttonChildrenContent}
      </Comp>
    );
  }
);

CKButton.defaultProps = defaultProps;
